<template>
  <Component
    :is="'b-modal'"
    :id="id"
    ref="rsModal"
    :visible="input"
    :size="size"
    :lazy="lazy"
    :no-enforce-focus="true"
    :no-close-on-backdrop="noCloseOnBackdrop"
    :no-close-on-esc="noCloseOnEsc"
    :centered="isCentered"
    :scrollable="scrollable"
    :dialog-class="dialogClass"
    :modal-class="modalClass"
    :body-class="bodyClass"
    class="rs-modal"
    :class="`rs-modal ${className}`"
    :data-backdrop="false"
    @shown="$emit('shown')"
    @hide="handleHide"
  >
    <template #modal-header>
      <div class="modal-header__left">
        <RsIcon
          v-show="infoIcon"
          :class="`fas mr-1 ${iconClass}`"
          style="color:DodgerBlue; font-size: 25px;"
          icon="info-circle"
        />
        <RsIcon
          v-if="icon"
          :icon="icon"
          style="font-weight: bold; margin-right: 5px; font-size: 25px;"
          :class="iconClass"
        />
        <slot name="modalTitle">
          <h5
            class="modal-title"
            v-html="title"
          />
        </slot>
        <slot name="headerRight" />
      </div>
      <div class="modal-header__right">
        <div class="modal-actions">
          <RsDropdownActionMenu
            v-show="dropdownItems.length > 0"
            horizontal
          >
            <!-- Property Modal -->
            <RsDropdownItem
              v-for="(item, idx) in dropdownItems"
              v-show="!item.isHidden"
              :key="idx"
              :title="item.title"
              :delete="item.isDelete"
              @click="$emit(item.emitName, item.emitData)"
            />
          </RsDropdownActionMenu>

          <div
            v-show="dropdownItems.length > 0"
            class="modal-actions__separator"
          />
          <RsButton
            v-if="headerButton && noCloseButton"
            class="reset"
            text
            @click="$emit('on-header-button-clicked')"
          >
            {{ headerButton }}
          </RsButton>
          <button
            v-if="!noCloseButton"
            type="button"
            title="Close"
            aria-label="Close"
            class="close"
            @click="onCloseButtonClick"
          >
            <RsIcon icon="times" />
          </button>
        </div>
      </div>
    </template>

    <slot />
    <!-- <rs-translation-message /> -->
    <template #modal-footer>
      <Fragment v-if="isUpdatePermitted && showMainFooter">
        <div class="modal-footer__left">
          <slot name="modal-footer-left" />
          <!-- A reset to Default Button -->
          <RsButton
            v-if="headerButton && !noCloseButton"
            class="rs-reset"
            text
            @click="$emit('on-header-button-clicked')"
          >
            {{ headerButton }}
          </RsButton>

          <!-- A Delete Button -->
          <RsButton
            v-if="deleteButton"
            :icon="deleteButton.icon"
            :weight="deleteButton.iconWeight ?? 'far'"
            :variant="deleteButton.variant"
            @click="$emit(deleteButton.emitName, deleteButton.emitData)"
          >
            {{ deleteButton.text }}
          </RsButton>
          <RsButton
            v-if="secondaryButton"
            :variant="secondaryButtonVariant || 'primary'"
            @click="$emit('on-secondary-button-clicked')"
          >
            {{ secondaryButton }}
          </RsButton>
          <span
            v-if="lastUpdated"
            class="small pr-3"
          >
            Last updated on {{ lastUpdatedFormatted }}
          </span>
          <span
            v-if="customLeftFooterText !== ''"
            class="small pr-3"
          >
            {{ customLeftFooterText }}
          </span>
          <RsToggleTranslation
            v-if="isTranslatable"
            use-select
            :is-english-model-valid="isModalValid"
            :edited-validator-object="editedValidatorObject"
            :compare-to-validator-object="compareToValidatorObject"
            @submit="$emit('submit')"
          />
          <!-- Only show if English is not selected. -->
          <span
            v-if="$rsGlobals.$language !== 'en' && !hideLanguageWarning"
            class="translation-text"
          >Editing non-English content</span>
        </div>
        <div class="modal-footer__right">
          <slot name="modal-footer">
            <RsButton
              v-if="isCancel"
              :id="id ? `btn_${id}_cancel` : ''"
              variant="cancel"
              @click="onCloseButtonClick"
            >
              {{ $ts('actions.cancel') }}
            </RsButton>
            <RsButton
              v-if="hasMainButtonRight"
              :id="id ? `btn_${id}_submit` : ''"
              :variant="secondaryButtonVariant"
              :disabled="disabled"
              @click="$emit('submit')"
            >
              <i
                v-if="buttonIcon"
                :class="`${buttonIcon} mr-2`"
              />
              {{ mainButtonText }}
            </RsButton>
          </slot>
        </div>
      </Fragment>
      <Fragment v-else>
        <div />
      </Fragment>
    </template>
  </Component>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { BModal, BvModalEvent } from 'bootstrap-vue';
import { IDropdownEmitOptions } from '@/shared';
import RsIcon from '@/components/_global/RsIcon.vue';
import { assertRef } from '@/shared/helpers/assert-ref';

dayjs.extend(advancedFormat);

export type TDeleteButtonDetails<T = string> = {
  text: string,
  variant: string,
  icon: string,
  iconWeight?: string,
  emitName: string,
  emitData: T,
};

export default defineComponent({
  name: 'RsModal',
  components: { RsIcon },
  props: {
    id: {
      type: [Number, String],
      default: '',
    },

    className: {
      type: String,
      default: '',
    },

    secondaryButtonVariant: {
      type: String,
      default: 'primary',
    },

    customLeftFooterText: {
      type: String,
      default: '',
    },

    title: {
      type: String,
      default: '',
    },

    icon: {
      type: String,
      default: '',
    },

    size: {
      type: String,
      default: '',
    }, // sm, md, lg, xl, etc.

    content: {
      type: String,
      default: '',
    },

    iconClass: {
      type: String,
      default: '',
    },

    infoIcon: {
      type: Boolean,
      default: false,
    },

    iconColor: {
      type: String,
      default: 'primary',
    },

    dataSaved: {
      type: Object,
      default: () => ({}),
    },

    tabIndex: {
      type: Number,
      default: 0,
    },

    tabs: {
      type: Boolean,
      default: false,
    },

    changed: {
      type: Boolean,
      default: false,
    },

    buttonText: {
      type: String,
      default: 'Save',
    },

    buttonIcon: {
      type: String,
      default: '',
    },

    secondaryButton: {
      type: String,
      default: '',
    },

    headerButton: {
      type: String,
      default: '',
    },

    deleteButton: {
      type: Object as PropType<TDeleteButtonDetails>,
      default: () => null,
    },

    lazy: {
      type: Boolean,
      default: false,
    },

    isModalValid: {
      type: Boolean,
      default: true,
    },

    isTranslatable: {
      type: Boolean,
      default: false,
    },

    isCancel: {
      type: Boolean,
      default: true,
    },

    value: {
      type: Boolean,
      default: false,
    },

    closeModal: {
      type: Boolean,
      default: false,
    },

    removableItems: {
      type: Array,
      default: () => [],
    },

    dropdownItems: {
      type: Array as PropType<IDropdownEmitOptions[]>,
      default: () => [],
    },

    isCompareDisabled: {
      type: Boolean,
      default: false,
    },

    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },

    noCloseButton: {
      type: Boolean,
      default: false,
    },

    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },

    editedValidatorObject: {
      type: Object,
      default: () => ({}),
    },

    compareToValidatorObject: {
      type: Object,
      default: () => ({}),
    },

    logObjectCompare: {
      type: Boolean,
      default: false,
    },

    lastUpdated: {
      type: [String, Date],
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    showMainFooter: {
      type: Boolean,
      default: true,
    },

    isCentered: {
      type: Boolean,
      default: false,
    },

    hasMainButtonRight: {
      type: Boolean,
      default: true,
    },

    scrollable: {
      type: Boolean,
      default: false,
    },

    dialogClass: {
      type: String,
      default: '',
    },

    modalClass: {
      type: String,
      default: '',
    },

    bodyClass: {
      type: String,
      default: '',
    },

    /**
     * Hide the non-english warning
     */
    hideLanguageWarning: {
      type: Boolean,
      default: false,
    },

    /**
     * Control whether the user has permission to update/save. When false, the footer with save button is hidden.
     */
    isUpdatePermitted: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      dataChange: false,
      showAlert: false,
      prevent: false,
      input: false,
      translationKey: 0,
    };
  },

  computed: {
    valid(): boolean {
      return !this.isModalDifferent;
    },

    mainButtonText(): string {
      // Preserve custom labels while localizing the component's default submit labels.
      if (this.buttonText === 'Save') {
        return this.$ts('actions.save') as string;
      }

      if (this.buttonText === 'Save Translations') {
        return this.$ts('translations.saveButton') as string;
      }

      return this.buttonText;
    },

    lastUpdatedFormatted(): string {
      return dayjs(this.lastUpdated).format('MMMM Do, YYYY');
    },
  },

  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.value) {
          this.input = true;
        } else {
          this.handleHide();
        }
      },
    },
    closeModal: {
      immediate: true,
      handler() {
        if (this.closeModal) {
          this.handleHide();
        }
      },
    },
  },
  methods: {
    async showWarningMsg(title: string = 'You have unsaved changes!') {
      return this.$swal
        .fire({
          title: `<i class="fas fa-exclamation-triangle rs-text-warning"></i> ${title}`,
          showDenyButton: true,
          showCancelButton: true,
          confirmButtonColor: '#00a889',
          confirmButtonText: `Confirm`,
          denyButtonText: `Continue without saving`,
          customClass: {
            container: 'swal-warning-container swal-warning-no-text',
            htmlContainer: 'swal-warning-body-container',
            title: 'swal-warning-title',
            actions: 'swal-warning-actions',
          },
        })
        .then((result: any) => result);
    },

    async showErrorMsg() {
      return this.$swal
        .fire({
          icon: 'error',
          title: 'Form is invalid.',
          confirmButtonColor: '#dd6b55',
          confirmButtonText: `OK`,
        })
        .then((result: any) => result);
    },

    async showSuccessMsg() {
      return this.$swal
        .fire({
          icon: 'success',
          title: 'Saved!',
          confirmButtonColor: '#00a889',
          confirmButtonText: `OK`,
        })
        .then((result: any) => result);
    },

    async handleHide(bvModalEvent?: BvModalEvent) {
      if (this.isModalDifferent() && this.input) {
        if (bvModalEvent) {
          bvModalEvent.preventDefault();
        }
        this.$rsGlobals.$rsSwal.warning({
          title: this.$rsGlobals.$isTranslateMode ? `You have unsaved translations!` : `${this.$ts('unsavedChanges.validTitle')}`,
          confirmHandler: async () => {
            if (this.isModalValid) {
              this.$emit('submit');
              await this.showSuccessMsg();
              this.closeModalManually();
            } else {
              await this.showErrorMsg();
              this.$emit('input', true);
            }
          },
          rejectHandler: () => {
            this.closeModalManually();
            this.$emit('on-reject-changes');
          },
          confirmWithoutSaveHandler: () => {
            this.$emit('input', true);
          },
          confirmButtonText: `${this.$ts('actions.confirm')}`,
          denyButtonText: `${this.$ts('unsavedChanges.continueWithoutSaving')}`,
        });
      } else {
        this.closeModalManually();
        this.$emit('on-reject-changes');
      }
    },

    closeModalManually(): void {
      this.input = false;
      this.$emit('input', false);
      this.$emit('cancel');
    },

    onCloseButtonClick() {
      assertRef(this.$refs.rsModal, BModal)?.hide();
      this.$emit('on-modal-close');
    },

    isModalDifferent(): boolean {
      return !this.isCompareDisabled && this.$utils.isObjectDifferent(
        this.$utils.cloneObject(this.editedValidatorObject ?? {}),
        this.$utils.cloneObject(this.compareToValidatorObject ?? {}),
        false,
        this.logObjectCompare,
      );
    },
  },
});
</script>
<style lang="scss">
@import "@/styles/_variables.scss";
$modal-padding: 1.5rem;
.modal-dialog {
  &.modal-fs {
    max-width: 100%;
    margin-left: 1rem;
    margin-right: 1rem;
  }
  &.modal-sp {
    max-width: 620px;
  }
  .modal-header {
    align-items: center;
    padding: 0.5rem $modal-padding;
    border-bottom: 1px #DEE2E6 solid;
    &__left {
      display: flex;
      align-items: center;
      h5 {
        margin: 0;
        font-size: 20px;
        font-weight: 700;
        display: inline-block;
      }
    }
    &__right {
      display: flex;
      align-items: center;
    }
    .modal-actions {
      display: flex;
      align-items: center;
      .close {
        position: relative;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        padding: 0;
        width: 40px;
        height: 40px;
        border-radius: $global-radius;
        color: $tertiary-muted;
        opacity: 0.4;
        transition: opacity 0.2s ease;
        &:hover {
          opacity: 1;
          background: $extra-light-gray;
        }
      }
      .reset {
        padding: 0;
        position: relative;
        font: normal normal bold 14px/19px Nunito;
        height: 19px;
        background: $white;
        color: $secondary-color;
        transition: opacity 0.2s ease;
        border: none;
        &:hover {
          background: $white;
        }
      }
      &__separator {
        width: 1px;
        height: 24px;
        background: rgba($extra-light-gray, 0.7);
        margin: 0 8px;
      }
      .rs-dropdown-action-menu {
        .dropdown-toggle {
          padding: 0;
          width: 40px;
          height: 40px;
          border-radius: $global-radius;
          opacity: 0.4;
          transition: all 0.2s ease;
          .rs-icon {
            color: $tertiary-muted;
          }
          &:hover {
            opacity: 1;
            background: $extra-light-gray;
          }
          &--open {
            background-color: $extra-light-gray;
            opacity: 1;
          }
        }
      }
    }
    .rs-avatar {
      width: 32px;
      height: 32px;
      font-size: 16px;
      margin-right: 0.5rem;
    }
  }
  .modal-body {
    min-height: 100%;
    padding: 1rem $modal-padding;
    p {
      font-size: 14px;
      color: $dark-gray;
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  .modal-footer {
    z-index: 1;
    padding: 0.5rem $modal-padding;
    &__left {
      display: flex;
      align-items: center;
      margin-right: auto;
      .rs-button {
        margin-right: 1rem;
      }
      .rs-select {
        margin: 0;
        width: 60px;
      }
      .translation-text {
        margin-left: 1rem;
        color: $error;
        font-size: 0.875rem;
      }
      .rs-reset {
        padding: 0;
        height: 19px;
        color: $secondary-color;
        border: none;
        &:hover {
          color: $secondary-color;
          background: $white;
        }
      }
    }
    &__right {
      .rs-button {
        margin: 0;
        &--cancel {
          margin-right: 0.813rem;
        }
      }
    }
  }

  .title-link {
    color: $secondary-color;
    font-weight: 700;
    align-self: center;

    .text-underline {
      text-decoration: underline;
    }
  }
}

// Styling For All Filter Modals
.navigation-trial, .tours-filter-modal-body{
  .tab-content {
    border-left: 1px solid #DBDBDB;
    padding-right: 24px;
  }
  .nav-tabs .nav-item:last-child {
    border-bottom: none !important;
  }
}

.navigation-trial > div:first-child, .tours-filter-modal-body > div:first-child{
  ul {
    li a {
      border-radius: 0;
      border: 0;
    }
  }
}

</style>
